import { TipCard } from "@/components/conversations/magic-moment/tip-card";
import { MagicMomentMessageEditor } from "@/components/conversations/magic-moment/magic-moment-message-editor";
import { ArrowLeft } from "lucide-react";
import type {
  AttentionBannerAction,
  AttentionBannerTip,
} from "./attention-banner";

interface BannerPanelBackProps {
  onBack: () => void;
}

function BannerPanelBack({ onBack }: BannerPanelBackProps) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="flex items-center gap-2 self-start font-body text-body-3 text-text-secondary transition-colors hover:text-text-primary"
    >
      <ArrowLeft className="size-4" aria-hidden />
      Back
    </button>
  );
}

interface AttentionTipsPanelProps {
  tips: readonly AttentionBannerTip[];
  onBack: () => void;
}

function AttentionTipsPanel({ tips, onBack }: AttentionTipsPanelProps) {
  return (
    <div className="flex w-full flex-col gap-3">
      <BannerPanelBack onBack={onBack} />
      <div className="flex flex-col gap-1">
        <h3 className="font-body text-body-2 font-bold text-text-primary">
          Tips
        </h3>
        <p className="font-body text-body-3 text-text-secondary">
          If you&apos;re not sure what to say, these are a good place to start.
        </p>
      </div>
      <div className="flex flex-col gap-2">
        {tips.map((tip) => (
          <TipCard key={tip.eyebrow} eyebrow={tip.eyebrow}>
            {tip.body}
          </TipCard>
        ))}
      </div>
    </div>
  );
}

interface AttentionMagicMomentPanelProps {
  teenGender: "male" | "female" | null;
  onBack: () => void;
  onInsertToChat?: (text: string) => void;
}

function AttentionMagicMomentPanel({
  teenGender,
  onBack,
  onInsertToChat,
}: AttentionMagicMomentPanelProps) {
  return (
    <div className="flex w-full flex-col gap-3">
      <BannerPanelBack onBack={onBack} />
      <div className="flex flex-col gap-1">
        <h3 className="font-body text-body-2 font-bold text-text-primary">
          Magic moment
        </h3>
        <p className="font-body text-body-3 text-text-secondary">
          One thoughtful message can make a teen feel really seen. Use this as a
          starting point and make it yours.
        </p>
      </div>
      <MagicMomentMessageEditor
        teenGender={teenGender}
        onInsertToChat={onInsertToChat}
      />
    </div>
  );
}

interface AttentionTipDetailPanelProps {
  label: string;
  body: string;
  onBack: () => void;
}

function AttentionTipDetailPanel({
  label,
  body,
  onBack,
}: AttentionTipDetailPanelProps) {
  return (
    <div className="flex w-full flex-col gap-3">
      <BannerPanelBack onBack={onBack} />
      <p className="font-mono text-xs font-medium tracking-[-0.24px] text-text-secondary">
        {label}
      </p>
      <TipCard>{body}</TipCard>
    </div>
  );
}

interface AttentionBannerDetailProps {
  action: Exclude<AttentionBannerAction, { kind: "launch" }>;
  teenGender?: "male" | "female" | null;
  onBack: () => void;
  onInsertMagicMoment?: (text: string) => void;
}

export function AttentionBannerDetail({
  action,
  teenGender = null,
  onBack,
  onInsertMagicMoment,
}: AttentionBannerDetailProps) {
  switch (action.kind) {
    case "magic_moment":
      return (
        <AttentionMagicMomentPanel
          teenGender={teenGender}
          onBack={onBack}
          onInsertToChat={onInsertMagicMoment}
        />
      );
    case "tips":
      return <AttentionTipsPanel tips={action.tips} onBack={onBack} />;
    case "tip":
      return (
        <AttentionTipDetailPanel
          label={action.label}
          body={action.body}
          onBack={onBack}
        />
      );
    default: {
      const exhaustive: never = action;
      return exhaustive;
    }
  }
}
